import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Header } from '../../components/Header';
import { Link } from 'react-router-dom';
import { SEO } from '../../components/SEO';
import { JsonLd } from '../../components/JsonLd';
import { ClickAnimation } from '../../components/ClickAnimation';
import { getImageUrl } from '../../lib/imageUrls';
import { getPersonSchema, getServiceSchema, getWebSiteSchema } from '../../lib/seo';
import { HOME_INTRO, HOME_STORAGE_KEY, PROFILE_TEXT } from '../../content/profileCopy';


interface Client {
  name: string;
  image: string;
  link?: string;
  visible: boolean;
  locked?: boolean;
}

// Client list with unlocked projects first, then locked projects at the end
const DEFAULT_CLIENTS: Client[] = [
  { name: "AI Playground", image: getImageUrl("/_images/3d-homepage/image-thumb-ai.png"), link: "/ai-playground", visible: true },
  { name: "Word Play", image: getImageUrl("/_images/WordPlay/wordplay-thumb.jpg"), link: "/word-play", visible: true },
  { name: "Mack Trucks", image: getImageUrl("/3d-homepage/img4.png"), link: "/mack-trucks", visible: true },
  { name: "Zeekr", image: getImageUrl("/3d-homepage/img8.png"), link: "/zeekr", visible: true },
  { name: "Volvo Cars", image: getImageUrl("/3d-homepage/img1.png"), visible: true, locked: true },
  { name: "Volvo Rider", image: getImageUrl("/3d-homepage/img2.png"), visible: true, link: "/volvo-rider" },
  { name: "Volvo Trucks AR", image: getImageUrl("/projects/3D-viewer/img16.png"), link: "/volvo-trucks-ar", visible: true },
  { name: "Formula 1", image: getImageUrl("/3d-homepage/img3.png"), link: "/formula1", visible: true },
  { name: "Drita Ime app", image: getImageUrl("/_images/DritaIme/dritaime-thumb-promo.png"), link: "/drita-ime", visible: true },
  { name: "Nivea", image: getImageUrl("/3d-homepage/img7.png"), visible: true, link: "/nivea" },
  { name: "Nike Tech Pack", image: getImageUrl("/projects/3D-viewer/img17.png"), visible: true, link: "/nike" },
  { name: "Polestar Engineered", image: "/projects/PolestarEngineered/PolestarEngineered-Thumb.png", visible: true, link: "/polestar-engineered" },
  { name: "Nissan IDx", image: getImageUrl("/projects/3D-viewer/img24.png"), visible: true, link: "/nissan-idx" },
  { name: "Volvo Trucks Aero", image: getImageUrl("/projects/3D-viewer/img18.png"), visible: true, link: "/volvo-trucks-aero" },
  { name: "Dieter Rams", image: getImageUrl("/projects/3D-viewer/img21.png"), visible: true, link: "/dieter-rams" },
  { name: "Volvo Trucks NA", image: getImageUrl("/projects/3D-viewer/img25.png"), visible: true, link: "/volvo-trucks-na" },
  { name: "H&M", image: getImageUrl("/3d-homepage/img11.png"), visible: true, locked: true },
  { name: "Mack Trucks AR", image: getImageUrl("/projects/3D-viewer/img22.png"), visible: true, locked: true },
  { name: "Lamborghini", image: getImageUrl("/3d-homepage/img12.png"), visible: true, locked: true },
  { name: "Other", image: getImageUrl("/projects/3D-viewer/img9.png"), visible: true, locked: true }
];

const DEFAULT_PROJECTS = DEFAULT_CLIENTS.filter(client => client.visible);

function getLocal(key: string, fallback: any) {
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : fallback;
  } catch {
    return fallback;
  }
}

const Home = (): JSX.Element => {
  const [intro, setIntro] = useState(HOME_INTRO);
  const [projects, setProjects] = useState(DEFAULT_PROJECTS);

  const [activeProject, setActiveProject] = useState<number>(0);
  const [showContent, setShowContent] = useState(false);
  const [clickAnimation, setClickAnimation] = useState<{ visible: boolean; position: { x: number; y: number } }>({
    visible: false,
    position: { x: 0, y: 0 }
  });
  const projectRefs = useRef<(HTMLLIElement | null)[]>([]);
  const projectListRef = useRef<HTMLUListElement>(null);
  const ticking = useRef(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    setIntro(getLocal(HOME_STORAGE_KEY, HOME_INTRO));
    setProjects(DEFAULT_PROJECTS);
  }, []);

  // Handle click animation
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // Only trigger animation for left mouse button clicks
      if (event.button !== 0) return;
      
      // Prevent conflicts with existing click handlers by using a small delay
      requestAnimationFrame(() => {
        setClickAnimation({
          visible: true,
          position: { x: event.clientX, y: event.clientY }
        });

        // Hide animation after it completes (1.08 seconds based on 27 frames at 25fps)
        setTimeout(() => {
          setClickAnimation(prev => ({ ...prev, visible: false }));
        }, 1080);
      });
    };

    // Use capture phase to ensure we catch clicks before other handlers
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      lastScrollY.current = window.scrollY;

      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const windowHeight = window.innerHeight;
          const middleY = windowHeight / 2;

          if (projectListRef.current) {
            const firstItemRect = projectRefs.current[0]?.getBoundingClientRect();
            const lastItemRect = projectRefs.current[projects.length - 1]?.getBoundingClientRect();

            if (firstItemRect && lastItemRect) {
              const shouldShow = 
                firstItemRect.top <= (middleY + 100) && 
                lastItemRect.bottom >= (middleY - 100);
              setShowContent(shouldShow);
            }

            let closestIndex = 0;
            let closestDistance = Infinity;

            projectRefs.current.forEach((ref, index) => {
              if (ref) {
                const rect = ref.getBoundingClientRect();
                const distance = Math.abs((rect.top + rect.bottom) / 2 - middleY);
                
                if (distance < closestDistance) {
                  closestDistance = distance;
                  closestIndex = index;
                }
              }
            });

            setActiveProject(closestIndex);
          }

          ticking.current = false;
        });

        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [projects.length]);

  const personImage = getImageUrl('/_images/bb25.jpg');

  return (
    <>
      <SEO 
        title={PROFILE_TEXT.seo.homeTitle}
        description={PROFILE_TEXT.seo.homeDescription}
        url="https://bes.art"
      />
      <JsonLd data={[getPersonSchema(personImage), getServiceSchema(), getWebSiteSchema()]} />
      <ClickAnimation 
        isVisible={clickAnimation.visible} 
        position={clickAnimation.position} 
      />
      <main className="relative w-full min-h-screen">
        {/* Fixed gradient background */}
        <div className="fixed inset-0 bg-white dark:bg-gradient-to-br dark:from-[#0a0a0a] dark:to-[#2d2d2d] -z-10" />
        
        <div className="relative w-full max-w-[1442px] min-h-screen pb-[600px] lg:pb-[800px] px-4 lg:px-8 mx-auto">
          <Header />
          
          {/* Bio section */}
          <section className="pt-[180px] lg:pt-[197px] lg:pl-[600px] lg:pr-8 relative z-40">
            <motion.p 
                className="w-[75%] lg:w-full max-w-[522px] [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white text-base lg:text-xl leading-5 lg:leading-7"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.3 }}
            >
              {intro}
            </motion.p>
          </section>

          {/* Portfolio image */}
          <div 
            className={`fixed top-1/2 right-2 w-[120px] h-[120px] -translate-y-1/2 md:w-[120px] md:h-[120px] md:right-4 lg:fixed lg:top-1/2 lg:left-0 lg:right-auto lg:-translate-y-1/2 lg:translate-x-[5%] lg:w-[590px] lg:h-[372px] lg:pr-0 transition-opacity duration-300 will-change-transform z-[60] ${
              showContent ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <motion.div
              className="border-0 shadow-none bg-transparent h-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: showContent ? 1 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {projects[activeProject].link ? (
                <Link to={projects[activeProject].link} className="block w-full h-full touch-manipulation">
                  <motion.img
                    className="w-full h-full object-contain aspect-square lg:object-contain will-change-contents cursor-pointer hover:opacity-80 active:opacity-60 transition-opacity touch-manipulation"
                    alt={`${projects[activeProject].name} project`}
                    src={projects[activeProject].image}
                    style={{
                      transition: 'opacity 0.3s ease-out',
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: showContent ? 1 : 0 }}
                    onTransitionEnd={() => {
                      // Set final opacity state after transition
                      return { opacity: showContent ? 1 : 0 };
                    }}
                  />
                </Link>
              ) : (
                <motion.img
                  className="w-full h-full object-contain aspect-square lg:object-contain will-change-contents"
                  alt={`${projects[activeProject].name} project`}
                  src={projects[activeProject].image}
                  style={{
                    transition: 'opacity 0.3s ease-out',
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: showContent ? 1 : 0 }}
                  onTransitionEnd={() => {
                    // Set final opacity state after transition
                    return { opacity: showContent ? 1 : 0 };
                  }}
                />
              )}
            </motion.div>
          </div>

          {/* Solid line - All breakpoints */}
          <div 
            className={`fixed top-1/2 left-0 right-0 z-50 transition-opacity duration-300 ${
              showContent ? 'opacity-50' : 'opacity-0'
            }`}
          >
            <div className="w-full border-t border-[#0048FF] h-[1px]" />
          </div>

          {/* Projects list */}
          <section className="relative mt-[20vh] lg:mt-0 lg:pt-[280px] pb-8 lg:pb-4 w-[75%] lg:w-auto lg:pl-[600px] z-40">
            <ul 
              ref={projectListRef} 
              className="[font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white text-3xl lg:text-6xl tracking-[-2px] lg:tracking-[-3px] leading-[48px] lg:leading-[84px]"
            >
              {projects.map((project, index) => (
                <li
                  key={index}
                  ref={el => projectRefs.current[index] = el}
                  className={`transition-all cursor-pointer will-change-transform ${
                    index === activeProject ? 'text-[#0048FF] translate-x-[5px]' : 'text-black dark:text-white opacity-20'
                  }`}
                >
                  {project.link ? (
                    <Link to={project.link} className="flex items-center gap-2">
                      {project.name}
                      {project.locked && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="opacity-60">
                          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                        </svg>
                      )}
                    </Link>
                  ) : (
                    <span className="flex items-center gap-2">
                      {project.name}
                      {project.locked && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="opacity-60">
                          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                        </svg>
                      )}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>

          {/* Footer */}
          <footer className="absolute bottom-[50px] lg:left-[640px] z-40">
            <div className="flex flex-wrap gap-4 md:gap-8">
              <a 
                href="mailto:hello@besbujupi.com"
                className="text-sm md:text-base text-black dark:text-white hover:opacity-70 transition-opacity [font-family:'ItemsTextTrial-Light',Helvetica] font-light"
              >
                Email
              </a>
              <a 
                href="https://www.instagram.com/besbujupi"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm md:text-base text-black dark:text-white hover:opacity-70 transition-opacity [font-family:'ItemsTextTrial-Light',Helvetica] font-light"
              >
                Instagram
              </a>
              <a 
                href="https://www.linkedin.com/in/besbujupi/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm md:text-base text-black dark:text-white hover:opacity-70 transition-opacity [font-family:'ItemsTextTrial-Light',Helvetica] font-light"
              >
                LinkedIn
              </a>
              <a 
                href="https://x.com/besbujupi"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm md:text-base text-black dark:text-white hover:opacity-70 transition-opacity [font-family:'ItemsTextTrial-Light',Helvetica] font-light"
              >
                X
              </a>
              <a 
                href="https://www.behance.net/besbujupi"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm md:text-base text-black dark:text-white hover:opacity-70 transition-opacity [font-family:'ItemsTextTrial-Light',Helvetica] font-light"
              >
                Behance
              </a>
            </div>
          </footer>
        </div>
      </main>
    </>
  );
};

export default Home;